import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { PrimaryButton, PrimaryPinkButton, WhiteButton } from "components/common/Button";
import { CameraModel } from "type/type";
import { getCameraModelOne, putCameraModel, deleteCameraModel, postCameraModel } from "api/api";
import Loading from "components/common/Loading";
import { useForm } from "react-hook-form";


const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  > div {
    padding: 0.5rem 1rem 1rem;
  }
  .title-h2 {
    font-family: var(--font-pretendard-bold);
    display: inline-flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  .process-content {
    > div {
      max-width: 700px;
    }
    input {
      min-width: 150px;
      max-width: 400px;
      // width: 50%;
    }

    .select-wrap {
      position: relative;
      min-width: 150px;
      max-width: 400px;
      width: 100%;

      &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        top: 50%;
        right: 20px;
        border: 5px solid transparent;
        border-top-color: #313a43;
        transform: scaleY(1.4);
      }
      &::before {
        content: '';
        position: absolute;
        display: block;
        width: 1px;
        height: 95%;
        top: 0;
        right: 48px;
        background-color: #e7e7e7;
        z-index: 0;
      }
    }

    &.event {
      label {
        font-size: 13px;
      }
      &.select-wrap {
        font-size: 14px;
        width: 60%;
      }
    }
  }
  
  .process-content2 {
    > div {
      max-width: 1400px;
    }
  }

  .err-border {
    border: 1px solid red !important;
  }
`;

const TitleAndBtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    gap: 0.5rem;
    &:first-of-type {
      flex-direction: column;
    }
    &:last-of-type {
      align-items: center;
    }
  }
`;

const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${prop => prop.theme.layer3BgColor};
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;
const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const InputWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.5rem;
  gap: 1rem;
  // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
  // border-bottom: 1px solid #666;

  label {
    width: 6rem;
  }
  label, input, select, textarea {
    display: inline-block;
    padding: 0.5rem;
  }
  textarea {
    resize: none;
    min-height: 10rem;
  }
  input:not([type="radio"]), textarea {
    width: calc(100% - 6rem);
    border: none;
    background-color: #f1f1f1;
    border-radius: 0.25rem;
    &:focus-visible {
      outline: none;
      &:not(:disabled, :read-only) {
        box-shadow: inset -1px -1px 1px 1px ${prop => prop.theme.primaryColor};
      }
    }
    &:read-only {
      background-color: inherit;
      border-bottom: 1px solid #666;
      border-radius: 0;
      // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
      // border-radius: 0.25rem;
      // background-color: ${prop => prop.theme.readOnlyColor}; 
      color: ${prop => prop.theme.rootTextColor};
      cursor: auto;
    }
  }
  
  select {
    border: none;
    border-radius: 0.25rem;
    background-color: #f1f1f1;
    
    &:focus-visible {
      outline: none;
      &:not(:disabled) {
        box-shadow: inset -1px -1px 1px 1px ${prop => prop.theme.primaryColor};
      }
    }
    &:disabled {
      background-color: inherit;
      border-bottom: 1px solid #666;
      border-radius: 0;
      // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
      // border-radius: 0.25rem;
      // background-color: ${prop => prop.theme.readOnlyColor}; 
      color: ${prop => prop.theme.rootTextColor};
      cursor: auto;
    }
  }

  input[type="radio"] {
    appearance: none;
    width: 1rem;
    height: 1rem;
    border-radius: 0.25rem;
    background-color: #fff;
    &:checked {
      background-color: ${prop => prop.theme.primaryColor};
    }
  }
  &.state-wrap {
    > div {
      align-items: center;
      gap: 0.5rem;
      label {
        width: auto;
      }
    }
  }
  &.as-content {
    span {
      width: calc(100% - 6rem);
      padding: 0.5rem;
      border-bottom: 1px solid #666;
      cursor: default;
      font-size: 14px;
      line-height: 20px;
      word-break: break-all;
      overflow-wrap: break-word;
    }
  }
`;

function AfterServiceProcess() {
  // 등록/수정 시 전달 값, url
  const navigate = useNavigate();
  // 목록에서 전달 받은 값
  const { cameraModelIdx } = useParams();
  const [loading, setLoading] = useState(true);
  
  // useForm
  const { register, formState: { errors, isValid }, trigger } = useForm<any>({
    mode: 'all',
  });

  const [cameraModel, setCameraModel] = useState<CameraModel>({
    idx: cameraModelIdx ? Number(cameraModelIdx) : 0,
    name: '', // 모델 명
    vendor: '', //제조사
    type: '실내', //NVR/DVR 구분
  });

  const handleChange = (event: any) => {
    const { name, value } = event.target;
  
    setCameraModel((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const _createCameraModel = async () => {
    if (!isValid) {
      trigger();
      return;
    }

    const response = await postCameraModel(cameraModel);
    if (response.code === 200) {
      alert("카메라 Model이 등록되었습니다.");
      navigate(-1);
    } else {
      alert(response.message);
    }
  };
  
  const _deleteCameraModel = async () => {
    const isDel = window.confirm(`${cameraModel.name}을 정말로 삭제하시겠습니까?`);
    if (isDel) {
      const response = await deleteCameraModel(Number(cameraModelIdx));
      if (response.code === 200) {
        alert("카메라 Model이 삭제되었습니다..");
        navigate(-1);
      } else {
        alert(response.message);
      }
    }
  };

  const _editCameraModel = async () => {
    if (!isValid) {
      trigger();
      return;
    }

    const response = await putCameraModel(cameraModel);
    if (response.code === 200) {
      alert("카메라 Model이 수정되었습니다.");
      navigate(-1);
    } else {
      alert(response.message);
    }
  };

  // AS 리스트 가져오기
  const getCameraModel = useCallback(async () => {
    try {
      setLoading(true); // 🔥 요청 시작 전에 딩 설정
      if (!cameraModelIdx) {
        return setLoading(false);
      }
      const cameraModelRes = await getCameraModelOne(Number(cameraModelIdx));
      if (cameraModelRes.code === 200) {
        setCameraModel(cameraModelRes.data);
      } else {
        alert(cameraModelRes.message);
      }
  
    } catch (error) {
      console.error("error", error);
    } finally {
      setLoading(false); // 🔥 요청이 끝나면 무조건 로딩 종료
    }
  }, [cameraModelIdx]);

  // 처음 useEffect 실행 및 페이지 변경 시 실행
  useEffect(() => {
    getCameraModel();
  }, [getCameraModel]);
  return (
    <>
    {loading ? <Loading /> :
      <Container>
        <TitleAndBtnContainer>
          <div>
            <p className='title-h2'>
              <span></span>
            </p>
          </div>
          <div>
            {!cameraModelIdx ?
              <PrimaryButton type="button" onClick={_createCameraModel}>저장</PrimaryButton>
            :
              <>
                <PrimaryButton type="button" onClick={_editCameraModel}>저장</PrimaryButton>
                <PrimaryPinkButton type="button" onClick={_deleteCameraModel}>삭제</PrimaryPinkButton>
              </>
            }
            <WhiteButton type="button" onClick={() => navigate(-1)}>목록</WhiteButton>
          </div>
        </TitleAndBtnContainer>
        <FlexCol>
          <FlexRow className="process-content">
            <InputWrap>
              <label>Model명</label>
              <input type='text' value={cameraModel.name} className={`${errors.name && 'err-border'}`} placeholder="Model명을 입력해주세요." maxLength={50}
                {...register("name", {
                  required: {
                    value: true,
                    message: 'Model명을 입력해주세요.'
                  },
                  onChange: handleChange
                })}
              />
            </InputWrap>
            <InputWrap>
              <label>제조사</label>
              <input type='text' value={ cameraModel.vendor } className={`${errors.vendor && 'err-border'}`} placeholder="제조사를 입력해주세요." maxLength={45}
                {...register("vendor", {
                  required: {
                    value: true,
                    message: '제조사를 입력해주세요.'
                  },
                  onChange: handleChange
                })}
              />
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content">
            <InputWrap>
              <label>구분</label>
              <div className="select-wrap">
                <select value={cameraModel.type}
                  {...register("type", {
                    required: true,
                    onChange: handleChange,
                  })}
                >
                <option value={'실내'}>실내</option>
                <option value={'실외'}>실외</option>
                <option value={'실내외'}>실내외</option>
                </select>
              </div>
            </InputWrap>
          </FlexRow>
        </FlexCol>
      </Container>
    }
    </>
  );
}

export default AfterServiceProcess;